export interface ApiConfig {
  baseURL: string;
  timeout: number;
}

/**
 * Discriminated union for API responses.
 *
 * Narrow on `success` before accessing `data` or `error`:
 *   const r = await service.doThing();
 *   if (r.success) useData(r.data);
 *   else setError(r.error.message);
 */
export type ApiResponse<T> =
  | { success: true;  data: T; message?: string }
  | { success: false; error: { message: string; code?: string; details?: Record<string, unknown> }; message?: string };

/**
 * What every rejected API call rejects with.
 *
 * A CLASS, not an interface, and `extends Error` deliberately. This was a
 * plain object literal, and the cost of that was paid in two places at once:
 *
 *  - Roughly ninety `catch (err) { err instanceof Error ? err.message : '…' }`
 *    sites across the app all took the false branch, so the server's own
 *    message ("Chemical is still referenced by 3 sprays") was thrown away and
 *    the user read a generic "Save failed" instead.
 *  - An unhandled rejection reached the crash reporter as a non-Error, was
 *    wrapped in `new Error(String(value))`, and arrived in the database as
 *    `Error: [object Object]` with a stack pointing at the reporter. Every
 *    such crash in the app fingerprinted to that one row.
 *
 * Nothing about how callers READ it changed — the fields are the same names in
 * the same places, and the two existing `err as ApiError` casts still narrow.
 * What changed is that `instanceof Error` is now true, which is what both of
 * the above were silently asking.
 */
export class ApiError extends Error {
  status: number;
  errors?: Record<string, string[]>;
  // Backend-provided error code (e.g. 'CREDENTIAL_GAPS', 'TNC_NOT_ACCEPTED').
  code?: string;
  // Full error payload from the backend — use for structured details that
  // don't fit the generic `message` / `errors` fields (e.g. credential gap
  // lists on a 422 response).
  details?: Record<string, unknown>;
  /**
   * No HTTP response came back at all — timeout, DNS, dropped connection.
   *
   * Distinct from `status`, which is 500 for BOTH a transport failure and a
   * genuine server error, so the status alone cannot tell a caller whether
   * retrying could possibly help. Only a request that never got an answer is
   * worth sending again; a 500 the server actually produced will be produced
   * again.
   */
  offline?: boolean;

  constructor(message: string, status: number) {
    super(message);
    // Part of the server-side fingerprint, so these group as their own kind
    // rather than melting into every other plain `Error`.
    this.name = 'ApiError';
    this.status = status;
  }
}

export interface PaginationParams {
  page: number;
  limit: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}
